import { useMemo, useState } from 'react';
import { Link, Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
  AppShell,
  Archive,
  House,
  Logo,
  NotificationMenu,
  Package,
  Plus,
  Question,
  User,
  Warehouse,
  timeAgo,
  type NavGroup,
  type NotificationEntry
} from '@nexvia-logistics/ui';

import { DASHBOARD_PATH } from 'config';
import { isStorageVisible } from 'domain/storage/storage-module';
import { ecommerceImportsEnabled } from 'shared/config/ecommerce';
import useAuth from 'hooks/useAuth';
import useDraftCount from 'hooks/useDraftCount';
import useNotifications from 'hooks/useNotifications';
import useShipmentQuotingCount from 'hooks/useShipmentQuotingCount';
import useStoragePublishedCount from 'hooks/useStoragePublishedCount';
import { useSocket } from 'hooks/useSocket';
import { useAppFeatures } from 'shared/queries/app-features';
import { getAvatarSrc } from 'utils/avatar';

import { NOTIFICATION_TONES, notificationHref, type AppNotification } from './notificationEntries';

interface AuthShape {
  logout: () => Promise<void>;
  user?: { name?: string; email?: string; avatar?: string } | null;
}

/**
 * The application shell.
 *
 * Replaces `layout/MainLayout/`, which was the Berry template's structure with the Nexvia
 * palette painted on: 22 files and 1.914 lines, of which about 735 were unreachable - a
 * horizontal menu bar behind a config flag no UI could set, a search field, a language
 * switcher, a fullscreen toggle and two upsell cards with no importers, and a nested-menu
 * renderer for a menu that has no nesting.
 *
 * Everything below is composition. The shell itself, the notification bell and its panel,
 * the account menu and the page container all come from `@nexvia-logistics/ui`; what stays
 * here is what only this app knows: which destinations exist, which counts go on them, what
 * a notification means, and where a click on one goes.
 */
const RAIL_KEY = 'nexvia:sidebar-collapsed';

/**
 * The rail's state, remembered. `MainLayout` kept it in a fake SWR resource
 * (`api/menu.ts`, a fetcher returning a local constant) that reset on every load, so a
 * folded sidebar unfolded itself on the next visit. Read in the initialiser rather than in
 * an effect, so the sidebar does not render open and then snap shut.
 */
function readRail(): boolean {
  try {
    return window.localStorage.getItem(RAIL_KEY) === 'true';
  } catch {
    return false;
  }
}

export default function AppLayout() {
  useSocket();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout, user } = useAuth() as unknown as AuthShape;

  const [collapsed, setCollapsed] = useState(readRail);
  const onCollapsedChange = (next: boolean) => {
    setCollapsed(next);
    try {
      window.localStorage.setItem(RAIL_KEY, String(next));
    } catch {
      // A browser that refuses storage still gets a working rail, just a forgetful one.
    }
  };

  const { count: draftCount } = useDraftCount();
  const { count: quotingCount } = useShipmentQuotingCount();
  // NEX-1645 (plan 2026-10-20, Tarea 3.3) — with the storage module `off` (also while GET /app/features loads
  // or fails) the client sees nothing of it: no «Depósitos» item nor its badge (whose
  // GET /storage/requests/mine is not even called) and no link to the depósito panel.
  // `registration_only` keeps them: a client manages what they already have (Johan's answer 8).
  const storageVisible = isStorageVisible(useAppFeatures().storage);
  const { count: storagePublishedCount } = useStoragePublishedCount({ enabled: storageVisible });
  const { notifications, unreadCount, loading, fetchNotifications, markAsRead, markAllRead } = useNotifications();

  /**
   * One flat group, no titles: Inicio, Mis solicitudes, Borradores and Guardado read as one
   * list, which is the NEX-1115 design. «+ Nueva solicitud» is not a destination, it is the
   * primary action, so it lives in `sidebarHeader`. The names cover every request type since
   * «Personas» (D-13). The storage design pass (spec §4.0) had renamed it «Publicar una
   * solicitud»; merging develop (2026-10-20) kept develop's «Nueva solicitud», the label every
   * other entry point (dashboard, drafts, list, wizard heading) already uses.
   */
  const groups = useMemo<NavGroup[]>(
    () => [
      {
        id: 'main',
        items: [
          { id: 'dashboard', label: 'Inicio', href: DASHBOARD_PATH, icon: <House size={20} /> },
          ...(ecommerceImportsEnabled
            ? [{ id: 'purchases', label: 'Compras de tiendas', href: '/purchases', icon: <Package size={20} /> }]
            : []),
          {
            id: 'view-shipments',
            label: 'Mis solicitudes',
            href: '/shipments',
            icon: <Package size={20} />,
            // A count of shipments being quoted is something to look at, not something
            // wrong, so it stays the informational tone rather than the shell's old red.
            badge: quotingCount ?? 0
          },
          {
            id: 'view-draft-shipments',
            label: 'Borradores',
            href: '/shipments/drafts',
            icon: <Archive size={20} />,
            badge: draftCount ?? 0
          },
          ...(storageVisible
            ? [
                {
                  id: 'view-storage',
                  label: 'Depósitos',
                  href: '/storage',
                  icon: <Warehouse size={20} />,
                  // Design pass (spec §4.0, D-4.2) — the published storage requests waiting for a quote choice.
                  badge: storagePublishedCount ?? 0
                }
              ]
            : [])
        ]
      }
    ],
    [draftCount, quotingCount, storagePublishedCount, storageVisible]
  );

  /**
   * Which item is current, with the same matching the old `NavItem` did.
   *
   * `end` is what keeps `/shipments` from lighting up on `/shipments/drafts` and on
   * `/shipments/123`, and it is per destination: `Inicio` matches its whole subtree, the
   * other two match exactly. On a route no item owns - a shipment detail, the wizard, the
   * account pages - nothing is marked, which is what happens today.
   */
  const activeId = useMemo(() => {
    const exact = ['/shipments', '/shipments/drafts', '/storage'];
    const match = groups[0]?.items.find((item) => matchPath({ path: item.href, end: exact.includes(item.href) }, pathname));
    return match?.id ?? '';
  }, [groups, pathname]);

  const entries = useMemo<NotificationEntry[]>(
    () =>
      (notifications as AppNotification[]).map((notification) => ({
        id: notification.id,
        title: notification.title,
        body: notification.body,
        time: timeAgo(notification.createdAt),
        read: !!notification.readAt,
        tone: NOTIFICATION_TONES[notification.type] ?? 'info'
      })),
    [notifications]
  );

  const onEntryClick = (entry: NotificationEntry) => {
    const notification = (notifications as AppNotification[]).find((n) => n.id === entry.id);
    if (!notification) return;
    markAsRead(notification.id);
    const href = notificationHref(notification);
    if (href) navigate(href);
  };

  return (
    <AppShell
      groups={groups}
      activeId={activeId}
      collapsible
      collapsed={collapsed}
      onCollapsedChange={onCollapsedChange}
      logo={
        <Link to={DASHBOARD_PATH} aria-label="Nexvia">
          <Logo height={26} />
        </Link>
      }
      /*
       * The isotype on its own. `ui-component/Logo` only draws the wordmark - one SVG with a
       * 158x36 viewBox - so the folded rail was showing "nexvia" in full, squeezed into 76px.
       * The package's own `Logo` has the mark, and it takes the brand colour from
       * `currentColor` instead of a hex baked into the artwork.
       */
      logoMark={
        <Link to={DASHBOARD_PATH} aria-label="Nexvia">
          <Logo variant="mark" height={30} />
        </Link>
      }
      user={{
        name: user?.name || 'Mi cuenta',
        email: user?.email,
        avatarUrl: getAvatarSrc(user) ?? undefined
      }}
      profileItems={[{ id: 'profile', label: 'Mi perfil', icon: <User size={18} />, onSelect: () => navigate('/profile') }]}
      onSignOut={async () => {
        try {
          await logout();
          navigate('/login');
        } catch (err) {
          console.error(err);
        }
      }}
      topbarActions={
        <NotificationMenu
          entries={entries}
          unreadCount={unreadCount}
          loading={loading}
          onOpen={() => fetchNotifications(1)}
          onEntryClick={onEntryClick}
          onMarkAllRead={markAllRead}
          onSeeAll={() => navigate('/notifications')}
        />
      }
      sidebarHeader={
        <button
          type="button"
          onClick={() => navigate('/shipment/new')}
          className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-pill border-0 bg-primary text-ui-small font-medium text-on-primary hover:bg-primary-dark"
        >
          <Plus size={18} />
          Nueva solicitud
        </button>
      }
      sidebarFooter={
        <div className="flex flex-col gap-3">
          <a
            href="mailto:soporte@nexvia.io"
            className="inline-flex items-center gap-2 text-ui-small font-medium text-on-surface-variant no-underline"
          >
            <Question size={19} />
            Ayuda y soporte
          </a>
        </div>
      }
      footer={
        <span className="flex items-center justify-between">
          <span className="text-ui-caption tracking-normal text-on-surface-variant">
            &copy; 2025 Nexvia. Todos los derechos reservados.
          </span>
          <span className="text-ui-caption tracking-normal text-on-surface-variant">v{__APP_VERSION__}</span>
        </span>
      }
      renderLink={(item, content, className, state) => (
        <Link to={item.href} aria-current={state.current ? 'page' : undefined} className={className}>
          {content}
        </Link>
      )}
    >
      <Outlet />
    </AppShell>
  );
}
